/**
 * The briefing (08 §2.3) as text: an operations order generated from the mission parameters.
 * Five paragraphs - SITUATION / MISSION / EXECUTION / EXTRACTION / TIME ON TARGET - built from
 * the archetype's template (orders.ts) and the names `describe` gives for the ids. Pure.
 *
 * Nothing here asks which archetype this is. What differs from mission to mission beyond its own template -
 * whether Recall works, whether there is a drop at all, what rides in a pod, whether a stated time is running -
 * is read from the mission's FACTS (src/meta/content/archetypeFacts.ts), so a new mission with the same rule
 * gets the same words.
 */

import type { MissionParams } from '../../../sim/types';
import { WALLS_LINE, weatherLine } from '../../hud/missionConditions';
import type { ArchetypeFacts } from '../archetypes';
import { archetypeInfo, clampThreat } from '../archetypes';
import type { Describe } from '../text';
import { safeDescribe } from '../text';
import type { BriefingGround } from './ground';
import { dogTeamsLine } from './dogLine';
import { ORDERS } from './orders';

export const BRIEFING_HEADINGS = ['SITUATION', 'MISSION', 'EXECUTION', 'EXTRACTION', 'TIME ON TARGET'] as const;
export type BriefingHeading = (typeof BRIEFING_HEADINGS)[number];

export interface BriefingSection {
  heading: BriefingHeading;
  lines: string[];
}

const STANDARD_EXTRACTION = [
  'RECALL THE OSPREY WHEN THE JOB IS DONE - OR WHEN IT HAS GONE WRONG. RECALL IS THE ABORT.',
  'IT ARRIVES 45 SECONDS AFTER THE CALL AND HOLDS 20 SECONDS OVER THE X. YOU STILL HAVE TO REACH THE LADDER.',
];

const POD_LETTERS = 'ABC';

/** TIME ON TARGET: when the clock starts, and what happens when it runs out - which depends on how Recall works. */
function clockLines(facts: ArchetypeFacts): { start: string; zero: string | null } {
  const start = facts.startCaptured
    ? 'THERE IS NO DROP: THE CLOCK STARTS THE MOMENT YOU ARE ON YOUR FEET.'
    : 'THE CLOCK STARTS AT TOUCHDOWN.';
  // Where the Osprey only ever comes at zero, the order's own EXTRACTION paragraph has already said so.
  if (facts.recall === 'at-clock-zero') return { start, zero: null };
  if (facts.recall === 'on-completion') {
    return {
      start,
      zero: 'THERE IS NO RECALL. IF THE CLOCK REACHES ZERO WITH THE JOB UNDONE THE OSPREY COMES ANYWAY, THE MISSION'
        + ' HAS NOT BEEN DONE, AND IT DOES NOT WAIT.',
    };
  }
  return { start, zero: 'IF YOU HAVE NOT RECALLED BY ZERO THE OSPREY COMES ANYWAY. IT DOES NOT WAIT.' };
}

/** A stated time on the ground clock (10 §1.1): said once more where the clock is discussed. */
function statedTimeLine(facts: ArchetypeFacts): string | null {
  if (facts.clock === 'deadline') {
    return 'THIS MISSION RUNS TO A STATED TIME AS WELL AS THE CLOCK. MISS IT AND THE MISSION IS LOST. READ THE'
      + ' COUNTDOWN ON YOUR HUD BEFORE YOU MOVE.';
  }
  if (facts.clock === 'event') {
    return 'SOMETHING ON THIS MISSION HAPPENS AT A STATED TIME. READ THE COUNTDOWN ON YOUR HUD BEFORE YOU MOVE.';
  }
  return null;
}

function garrison(threat: number): string {
  if (threat <= 3) return 'LIGHT GARRISON, SLOW TO REACT.';
  if (threat <= 6) return 'ALERT REGULARS. SPECIALISTS - FLAMETHROWERS, ROCKET TROOPERS - FROM THREAT 5.';
  return 'HEAVY GARRISON: FAST, ACCURATE, WITH SPECIALISTS AND AUTOMATED DEFENCES.';
}

function named(describe: Describe, id: string): string {
  const d = safeDescribe(describe, id);
  return d.text === '' ? `${d.name}.` : `${d.name}: ${d.text}`;
}

/**
 * Tag Recovery (04 §2.3): the optional objective, its one condition and what it is worth. It
 * rides under MISSION so the order keeps its five headings.
 */
export function tagRecoveryLines(name: string): string[] {
  return [
    `OPTIONAL OBJECTIVE - TAG RECOVERY: ${name.toUpperCase()} FELL ON THIS GROUND AND HIS MARKER STANDS SOMEWHERE ON`
      + ' THE MAP. REACH IT AND RECOVER HIS TAGS. THEY COUNT ONLY IF YOU GET HOME YOURSELF.',
    'REWARD: HIS MEDALS ARE CONFIRMED ON THE MEMORIAL WALL, AND ONE OF HIS PERKS CAN BE TAUGHT TO A LIVING RANGER.'
      + ' THE MISSION DOES NOT DEPEND ON IT.',
  ];
}

/**
 * The whole order. Text is mixed case where it comes from `describe`; the screen sets capitals. `ground` is what
 * the map itself holds (`groundOf`): the screen passes it; without it the order says nothing about the ground.
 */
export function briefingText(
  params: MissionParams, describe: Describe, ground: BriefingGround | null = null,
): BriefingSection[] {
  const order = ORDERS[params.archetype];
  const threat = clampThreat(params.threat);
  const name = (id: string): string => safeDescribe(describe, id).name;

  const situation = [
    `TARGET: ${name(params.archetype)}. TERRAIN: ${name(params.biome)}. LIGHT: ${name(params.timeOfDay)}.`
      + ` WEATHER: ${name(params.weather)}.`,
    order.situation,
    `THREAT RATING ${threat} OF 9. ${garrison(threat)}`,
  ];
  // A storm is named on the line above and costs both sides something; say what, here, where no
  // panel height can drop it. The Storm card says it too, but Practice deals no card.
  const sky = weatherLine(params.weather);
  if (sky !== null) situation.splice(1, 0, sky);
  if (archetypeInfo(params.archetype).biome !== params.biome) {
    situation.push('VARIANT: THIS TARGET IS NOT IN ITS USUAL TERRAIN. EXPECT THE LOCAL EQUIVALENT OF EVERY OBSTACLE.');
  }
  for (const id of params.modifiers) situation.push(`CONDITION - ${named(describe, id)}`);
  for (const id of params.intel) situation.push(`INTEL HELD - ${named(describe, id)}`);

  const { facts } = archetypeInfo(params.archetype);
  const execution = [
    ...order.execution,
    ...order.special(params).map((line) => `SPECIAL INSTRUCTION: ${line}`),
    ...(order.equipment ?? []).map((line) => `EQUIPMENT: ${line}`),
  ];
  // ROUND7 §4: the same words as the standing orders, which may drop the line when a crowded panel runs short.
  if (ground?.walls === true) execution.push(WALLS_LINE);
  // ROUND8: the teams the roguelike layer put on this mission - the number the generator places.
  const dogs = dogTeamsLine(params.dogTeams ?? 0, params.biome === 'arctic');
  if (dogs !== null) execution.push(dogs);
  if (params.modifiers.includes('radio-silence')) {
    execution.push(facts.startCaptured
      ? 'RADIO SILENCE: NO MINIMAP AND NO MARKERS, AND NO PASS OVER THE TARGET TO STUDY THE GROUND FROM.'
      : 'RADIO SILENCE: NO MINIMAP AND NO MARKERS. STUDY THE GROUND ON THE PASS OVER THE TARGET.');
  }
  // Inside Man (04 §8): the briefing has only the parameters, so it states the card's promise; the
  // standing orders in the field have the mission itself and say where the gate is (`missionConditions.ts`).
  if (params.intel.includes('inside-man')) {
    execution.push('INSIDE MAN: ONE GATE IN THE PERIMETER HAS BEEN LEFT OPEN AND THE SENTRY ON IT DRAWN AWAY.'
      + ' IT IS MARKED AS A PALE LANE ON YOUR STRIP MAP, AS A FORD IS.');
  }
  if (params.modifiers.includes('two-pod-limit')) {
    const item = facts.questItem;
    execution.push(item === null
      ? 'ONE FEWER SUPPLY POD IS AVAILABLE.'
      : `ONE FEWER SUPPLY POD IS AVAILABLE. POD ${POD_LETTERS[item.pod] ?? 'A'} STILL FLIES:`
        + ` THE ${item.name} RIDES IN IT.`);
  }

  const extraction = [...(order.extraction ?? STANDARD_EXTRACTION)];
  if (params.modifiers.includes('hot-extraction')) {
    extraction.push('THE PICKUP ZONE IS CONTESTED: WHEN THE AIRCRAFT TURNS INBOUND THE GARRISON GOES TO A FULL ALARM AND A'
      + ' SQUAD CONVERGES ON THE X. THE AIRCRAFT HOLDS OVER IT FOR 12 SECONDS, NOT 20.');
  }
  // ROUND5: this said "A SECOND X IS OPEN" over a sim that held one pickup point. There are two now
  // (`gen/altLz.ts`), so it can also say the rule the aircraft goes by, which is what he must plan on.
  if (params.intel.includes('alternate-lz')) {
    extraction.push('ALTERNATE LZ: A SECOND X IS OPEN, AWAY ON THE FAR SIDE OF THE OBJECTIVE. THE AIRCRAFT COMES TO'
      + ' WHICHEVER OF THE TWO IS NEARER YOU WHEN IT IS CALLED, AND THEN IT DOES NOT CHANGE ITS MIND.');
  }
  extraction.push('MISS THE AIRCRAFT AND YOU ARE STRANDED. THE SOUTH EDGE, ON FOOT, IS THE ONLY OTHER WAY HOME.');

  const clock = clockLines(facts);
  const time = [clock.start];
  if (threat <= 2) time.push('LIGHT OPPOSITION: COMMAND ALLOWS 15 % MORE TIME.');
  else if (threat >= 7) time.push('HEAVY OPPOSITION: THE WINDOW IS 10 % SHORTER.');
  if (params.modifiers.includes('short-fuse')) time.push('SHORT FUSE: A QUARTER OF THE CLOCK IS ALREADY GONE.');
  if (order.time !== undefined) time.push(order.time);
  const stated = statedTimeLine(facts);
  if (stated !== null) time.push(stated);
  if (clock.zero !== null) time.push(clock.zero);

  const mission = [order.mission(params)];
  if (params.tagRecovery !== undefined) mission.push(...tagRecoveryLines(params.tagRecovery.name));

  return [
    { heading: 'SITUATION', lines: situation },
    { heading: 'MISSION', lines: mission },
    { heading: 'EXECUTION', lines: execution.map((line, i) => `${i + 1}. ${line}`) },
    { heading: 'EXTRACTION', lines: extraction },
    { heading: 'TIME ON TARGET', lines: time },
  ];
}

/** The order as one string, headings on their own lines: what the typewriter types. */
export function briefingPlain(sections: readonly BriefingSection[]): string {
  return sections.map((s) => `${s.heading}\n${s.lines.join('\n')}`).join('\n\n');
}
